import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    linkedSignal,
    signal,
    untracked,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ModalComponent } from "../../modules/modal/modal.component";
import { ModalHeaderComponent } from "../../modules/modal/modal.header.component";
import { S25RichTextEditorModule } from "../../modules/s25-rich-text-editor/s25.rich.text.editor.module";
import { S25CheckboxModule } from "../../modules/s25-checkbox/s25.checkbox.module";
import { S25PipesModule } from "../../pipes/s25.pipes.module";
import { S25LoadingInlineModule } from "../../modules/s25-loading/s25.loading.inline.module";
import { S25ModalModule } from "../../modules/s25-modal/s25.modal.module";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { WelcomeMessageService } from "../../services/welcome.message.service";
import { FlsStore } from "../../stores/fls.store";
import { LoginStore } from "../../stores/login.store";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";

@Component({
    template: `
        <s25-modal-header [data]="params()"></s25-modal-header>
        <div class="modal-body">
            <div class="ngMessageArea">
                @if (editing()) {
                    <s25-ng-rich-text-editor [(modelValue)]="workingMessage" />
                } @else if (welcomeMessage.resource.isLoading()) {
                    <s25-ng-loading-inline-static />
                } @else {
                    <div
                        class="ngWelcomeMessagePreview"
                        [innerHTML]="workingMessage() ?? 'No Message to Display' | safeHTML"
                    ></div>
                }
            </div>
            @if (editing()) {
                <div class="c-margin-top--single c-modal--infoText">
                    <s25-ng-icon [type]="'info'" [ariaLabel]="false" size="1.25em" />
                    <span
                        >This is the global welcome message. You can also set custom welcome messages for individual
                        security groups in
                        <a [href]="groupsUrl" target="_blank" class="ngAnchor">25Live Groups</a>.</span
                    >
                </div>
            }
            @if (login.loggedIn() && !autoShow.resource.isLoading()) {
                <div class="c-displayInlineBlock c-floatRight">
                    <s25-ng-checkbox
                        [value]="autoShow.value()"
                        (valueChange)="saveAutoShow($event)"
                        labelId="showonlogin"
                    >
                        Show on Login
                    </s25-ng-checkbox>
                </div>
            }
        </div>
        <s25-ng-modal-footer [content]="footerTemplate" [bordered]="true" />
        <ng-template #footerTemplate>
            <div class="footerContainer">
                @if (canEdit()) {
                    @if (editing()) {
                        <button class="aw-button aw-button--outline" (click)="editMode.set('preview')">Preview</button>
                    }
                    <button
                        class="aw-button aw-button--outline"
                        [disabled]="globalWelcome.resource.isLoading() || editing()"
                        (click)="editMode.set('edit')"
                    >
                        Edit
                    </button>
                }
                @if (editing() || previewing()) {
                    <button class="aw-button aw-button--primary" [disabled]="saving()" (click)="save()">Save</button>
                } @else {
                    <button class="aw-button aw-button--primary" (click)="params()?.closeModal()">Close</button>
                }
            </div>
        </ng-template>
    `,
    styles: `
        .footerContainer {
            display: flex;
            justify-content: flex-end;
        }
        .ngMessageArea {
            padding-bottom: 20px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ModalHeaderComponent,
        S25RichTextEditorModule,
        S25CheckboxModule,
        S25PipesModule,
        S25LoadingInlineModule,
        S25ModalModule,
        S25IconComponent,
        FormsModule,
    ],
})
export class WelcomeMessageModalComponent extends ModalComponent {
    static readonly size = "md";
    static readonly title = " ";
    static readonly windowClass = "welcome-message";

    readonly login = inject(LoginStore);
    readonly fls = inject(FlsStore);

    readonly welcomeMessage = s25Resource(() => WelcomeMessageService.getWelcomeMessage());
    readonly autoShow = s25Resource(() => WelcomeMessageService.hasAutoShowWelcomeMessage());
    readonly globalWelcome = s25Resource({
        if: () => !!this.editMode(),
        loader: () => WelcomeMessageService.getGlobalWelcomeMessage(),
    });

    readonly canEdit = computed(() => this.fls.hasFull("SYS_MOTD"));
    readonly editing = computed(() => this.editMode() === "edit");
    readonly previewing = computed(() => this.editMode() === "preview");
    readonly editMode = signal<"edit" | "preview" | false>(false);
    readonly workingMessage = linkedSignal(
        () => this.globalWelcome.value() || this.welcomeMessage.value() || "Loading...",
    );
    readonly saving = signal(false);

    readonly groupsUrl = S25Util.getURL("", "/groups");

    constructor() {
        super();
        effect(() => {
            const newTitle = this.editing() ? "Edit Welcome Message" : " ";
            if (untracked(this.params)?.title !== newTitle) {
                this.params.update((params) => ({
                    ...params!,
                    title: newTitle,
                }));
            }
        });
    }

    async save() {
        try {
            this.saving.set(true);
            await WelcomeMessageService.setWelcomeMessage(this.workingMessage());
            this.data.closeModal();
        } finally {
            this.saving.set(false);
        }
    }

    async saveAutoShow(value: boolean) {
        await WelcomeMessageService.setAutoShowWelcomeMessage(value ? "T" : "F");
        this.autoShow.resource.set(value);
    }
}
